import type { Session, User } from '@supabase/supabase-js'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { supabase } from './supabase'

export type Role = 'owner' | 'member' | 'actor' | 'supervisor'

export interface SignInWithEmailOptions {
  /** Token threaded through `/auth/callback?invite=` so we land on /accept-invite. */
  inviteToken?: string
  /** Path the callback should bounce to (default: `/`). */
  redirectTo?: string
}

export interface AuthContextValue {
  session: Session | null
  user: User | null
  role: Role | null
  loading: boolean
  signInWithEmail: (email: string, opts?: SignInWithEmailOptions) => Promise<void>
  /**
   * OAuth login (Supabase OAuth provider). Currently only Google is wired —
   * the provider must be enabled in Supabase Auth → Providers.
   * Bounces through `${origin}/auth/callback` like magic-link.
   */
  signInWithProvider: (provider: 'google', opts?: SignInWithEmailOptions) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

/**
 * Provides the current Supabase session, derived `role`, and auth actions.
 * The role is read from `user.user_metadata.primary_role`, populated server-side
 * on invitation acceptance. Until the initial `getSession()` resolves, `loading`
 * is true and consumers should render a placeholder (the root route does this).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    void supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return
      setSession(data.session)
      setLoading(false)
    })
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      if (mounted) setSession(s)
    })
    return () => {
      mounted = false
      sub.subscription.unsubscribe()
    }
  }, [])

  const signInWithEmail = useCallback(async (email: string, opts?: SignInWithEmailOptions) => {
    const params = new URLSearchParams()
    if (opts?.redirectTo) params.set('next', opts.redirectTo)
    if (opts?.inviteToken) params.set('invite', opts.inviteToken)
    const qs = params.toString()
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback${qs ? `?${qs}` : ''}`,
      },
    })
    if (error) throw error
  }, [])

  const signInWithProvider = useCallback(
    async (provider: 'google', opts?: SignInWithEmailOptions) => {
      const params = new URLSearchParams()
      if (opts?.redirectTo) params.set('next', opts.redirectTo)
      if (opts?.inviteToken) params.set('invite', opts.inviteToken)
      const qs = params.toString()
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback${qs ? `?${qs}` : ''}`,
        },
      })
      if (error) throw error
    },
    [],
  )

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }, [])

  const role = useMemo<Role | null>(() => {
    const raw = session?.user?.user_metadata?.['primary_role']
    if (raw === 'owner' || raw === 'member' || raw === 'actor' || raw === 'supervisor') return raw
    return null
  }, [session])

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      role,
      loading,
      signInWithEmail,
      signInWithProvider,
      signOut,
    }),
    [session, role, loading, signInWithEmail, signInWithProvider, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be inside AuthProvider')
  return ctx
}

/**
 * Throws a `NotAuthorizedError` if the current user's role is not in `allowed`.
 * Use inside route components after ensuring the user is signed in. Returns the
 * role for ergonomics in the call site.
 */
export function useRequireRole(allowed: readonly Role[]): Role {
  const { role, loading } = useAuth()
  if (loading) throw new Promise(() => {}) // suspends until session resolves
  if (!role || !allowed.includes(role)) {
    throw new NotAuthorizedError(`requires one of: ${allowed.join(', ')}; got: ${role ?? 'none'}`)
  }
  return role
}

export class NotAuthorizedError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'NotAuthorizedError'
  }
}
